import { useEffect, useMemo, useRef } from 'react';

import {
  useExplorationActions,
  useExplorationState,
  useFilteredVolumes,
} from '../../context/ExplorationContext';
import { useContainerWidth } from '../../hooks/useContainerSize';
import { useExplorationTooltipHandlers } from '../../hooks/useExplorationTooltipHandlers';
import { buildCoverageGroups, enrichCoverageWrappers } from '../../lib/coverage/buildCoverageGroups';
import { buildCoverageInstances } from '../../lib/coverage/classifyInstance';
import type { DrillLocation } from '../../types/exploration';
import { renderCoverageView } from './renderCoverageView';

export default function CoverageCanvas() {
  const state = useExplorationState();
  const actions = useExplorationActions();
  const filtered = useFilteredVolumes();
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useContainerWidth(containerRef);
  const { showTooltip, hideTooltip } = useExplorationTooltipHandlers();

  const wrappers = useMemo(() => {
    const instances = buildCoverageInstances(filtered);
    const groups = buildCoverageGroups(
      instances,
      state.coverageGrouping,
      state.groupByAccount
    );
    return enrichCoverageWrappers(groups);
  }, [filtered, state.coverageGrouping, state.groupByAccount]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !width) return;

    return renderCoverageView(el, wrappers, {
      onHexClick: (inst) => {
        const instanceVols = filtered.filter((v) => v.instanceId === inst.instanceId);
        const location: DrillLocation = inst.cluster
          ? {
              structural: {
                kind: 'cluster',
                top: { kind: 'cluster', name: inst.cluster },
                nodeGroup: inst.nodeGroup && inst.nodeGroup !== inst.cluster ? inst.nodeGroup : undefined,
                instanceId: inst.instanceId,
              },
            }
          : { structural: { kind: 'cluster', top: { kind: 'ec2' }, instanceId: inst.instanceId } };
        actions.drillFromCoverage(location);
        actions.selectInstance(inst.instanceId, instanceVols);
      },
      showTooltip,
      hideTooltip,
    });
  }, [wrappers, width, filtered, actions.drillFromCoverage, actions.selectInstance, showTooltip, hideTooltip]);

  return (
    <div
      ref={containerRef}
      id="canvas-coverage"
      className="exploration-canvas coverage-canvas"
    />
  );
}
